/**
 * Add or edit one delivery address.
 *
 * This is what gets printed on a label and handed to a courier, so the validation is the
 * courier's: a usable street line and a real postcode. The same pure module the save path
 * uses runs as you type, and the blocking reason is always visible.
 */
import React, { useMemo, useState } from 'react';
import { X, AlertTriangle, MapPin } from 'lucide-react';
import { CustomerService } from '../services/CustomerService';
import { validateAddress } from '../logic/customerProfileFields';

const EditAddressModal = ({ mobile, address, index, actor = 'staff', onClose, onSaved }) => {
    const isNew = index == null || index < 0;
    const [form, setForm] = useState({
        fullAddress: address?.fullAddress || address?.address || '',
        city: address?.city || '',
        state: address?.state || '',
        pincode: address?.pincode || '',
        country: address?.country || 'India',
        landmark: address?.landmark || '',
        label: address?.label || '',
    });
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState('');

    const set = (k, v) => { setForm(f => ({ ...f, [k]: v })); setErr(''); };
    const check = useMemo(() => validateAddress(form), [form]);

    const save = async () => {
        if (!check.ok) { setErr(check.error); return; }
        setBusy(true); setErr('');
        try {
            if (isNew) await CustomerService.addNewAddress(mobile, check.values);
            else await CustomerService.updateAddress(mobile, index, check.values);
            onSaved?.();
            onClose?.();
        } catch (e) { setErr(e.message || 'Could not save the address.'); }
        finally { setBusy(false); }
    };

    const input = 'w-full border border-slate-200 rounded-lg px-3 py-2 text-[13px] mt-1';
    const isIndia = /^india$/i.test(form.country);

    return (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
            <div className="bg-white rounded-2xl w-full max-w-lg max-h-[92vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
                <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-2">
                    <MapPin size={16} className="text-purple-600" />
                    <h3 className="font-bold text-slate-800">{isNew ? 'Add a delivery address' : 'Edit delivery address'}</h3>
                    <button type="button" onClick={onClose} className="ml-auto text-slate-400 hover:text-slate-700"><X size={18} /></button>
                </div>

                <div className="p-4 space-y-3">
                    <div>
                        <label className="text-[12px] font-semibold text-slate-600">Full address</label>
                        <textarea
                            rows={3} value={form.fullAddress} onChange={e => set('fullAddress', e.target.value)}
                            className={input} placeholder="House / building, street, area"
                        />
                        {check.field === 'fullAddress'
                            ? <p className="text-[11px] text-red-600 mt-1">{check.error}</p>
                            : <p className="text-[11px] text-slate-400 mt-1">Written the way a courier needs to find it.</p>}
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="text-[12px] font-semibold text-slate-600">City / town</label>
                            <input value={form.city} onChange={e => set('city', e.target.value)} className={input} />
                        </div>
                        <div>
                            <label className="text-[12px] font-semibold text-slate-600">State</label>
                            <input value={form.state} onChange={e => set('state', e.target.value)} className={input} />
                        </div>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="text-[12px] font-semibold text-slate-600">{isIndia ? 'Pincode' : 'Postal / ZIP code'}</label>
                            <input value={form.pincode} onChange={e => set('pincode', e.target.value)} className={input} placeholder={isIndia ? '689549' : ''} />
                            {check.field === 'pincode' && <p className="text-[11px] text-red-600 mt-1">{check.error}</p>}
                        </div>
                        <div>
                            <label className="text-[12px] font-semibold text-slate-600">Country</label>
                            <input value={form.country} onChange={e => set('country', e.target.value)} className={input} />
                        </div>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="text-[12px] font-semibold text-slate-600">Landmark <span className="font-normal text-slate-400">(optional)</span></label>
                            <input value={form.landmark} onChange={e => set('landmark', e.target.value)} className={input} placeholder="Near the temple" />
                        </div>
                        <div>
                            <label className="text-[12px] font-semibold text-slate-600">Label <span className="font-normal text-slate-400">(optional)</span></label>
                            <input value={form.label} onChange={e => set('label', e.target.value)} className={input} placeholder="Home / Office" />
                        </div>
                    </div>

                    {err && <p className="text-[12px] text-red-600 flex items-start gap-1.5"><AlertTriangle size={14} className="mt-0.5 shrink-0" /> {err}</p>}
                </div>

                <div className="px-4 py-3 border-t border-slate-100">
                    {!check.ok && <p className="text-[12px] text-amber-800 mb-2 flex items-start gap-1.5"><AlertTriangle size={14} className="mt-0.5 shrink-0" /> {check.error}</p>}
                    <div className="flex gap-2">
                        <button type="button" onClick={onClose} className="px-3 py-2 rounded-lg border border-slate-200 text-slate-600 text-[12.5px] font-semibold">Cancel</button>
                        <button type="button" disabled={busy} onClick={save} className="flex-1 px-3 py-2 rounded-lg bg-purple-600 text-white text-[12.5px] font-semibold disabled:opacity-60">
                            {busy ? 'Saving…' : isNew ? 'Add address' : 'Save address'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default EditAddressModal;
